import gsap from 'gsap';
import SplitText from 'gsap/SplitText';
import Swiper from 'swiper';
import { Scrollbar } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/scrollbar';

gsap.registerPlugin(SplitText);

let isMenuOpen = false;
let hamburgerButton = null;
let mobileNav = null;
let splitTextInstances = [];
let cantineSwiperInstance = null;
const preventMenuScrollHandler = (event) => {
  if (!isMenuOpen) return;
  event.preventDefault();
};

export function initHamburgerMenu() {
  // Pulisci eventuali listener precedenti
  cleanup();
  
  hamburgerButton = document.querySelector('.hb-menu');
  if (!hamburgerButton) return;

  // Crea il markup del menu mobile se non esiste
  createMobileNavMarkup();
  
  // Inizializza elementi
  mobileNav = document.querySelector('.mobile-nav');
  
  if (!mobileNav) return;

  const initialMobileLinks = mobileNav.querySelectorAll('a');
  if (initialMobileLinks.length) {
    gsap.set(initialMobileLinks, {
      opacity: 0,
      visibility: 'hidden'
    });
  }

  // Initialize logo state based on screen size
  initLogoState();

  // Event listeners
  hamburgerButton.addEventListener('click', toggleMenu);
  
  // Gestisci i link del menu mobile
  handleMobileNavigation();
  
  // Inizializza interazioni per i loghi delle cantine
  initCantineLogosInteraction();
  
  // Gestisci resize per chiudere il menu su desktop
  window.addEventListener('resize', handleResize);
  
  // Gestisci ESC key
  document.addEventListener('keydown', handleEscKey);
}

function createMobileNavMarkup() {
  // Controlla se esiste già
  if (document.querySelector('.mobile-nav')) return;
  
  // Trova il header-content
  const headerContent = document.querySelector('.header-content');
  if (!headerContent) return;
  
  // Trova il menu esistente
  const mainMenu = document.querySelector('#main-menu');
  if (!mainMenu) return;
  
  const existingNav = mainMenu.querySelector('ul');
  if (!existingNav) return;
  
  // Clona il contenuto del menu
  const mobileNavContainer = document.createElement('div');
  mobileNavContainer.className = 'mobile-nav';
  
  // Clona la struttura del menu
  const clonedNav = existingNav.cloneNode(true);
  mobileNavContainer.appendChild(clonedNav);
  
  // Sezioni extra: loghi cantine (il selettore lingua è ora nell'header)
  const extrasWrapper = document.createElement('div');
  extrasWrapper.className = 'mobile-extras';

  // Loghi Cantine
  const cantineSource = document.getElementById('cantine-logos-source');
  if (cantineSource && cantineSource.innerHTML.trim()) {
    const cantineSection = document.createElement('div');
    cantineSection.className = 'mobile-cantine-logos';

    const sliderEl = document.createElement('div');
    sliderEl.className = 'mobile-cantine-swiper swiper';

    const wrapperEl = document.createElement('div');
    wrapperEl.className = 'swiper-wrapper';

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = cantineSource.innerHTML;

    const logoItems = tempContainer.querySelectorAll('li');

    logoItems.forEach((item) => {
      const slideEl = document.createElement('div');
      slideEl.className = 'swiper-slide';
      slideEl.innerHTML = item.innerHTML;
      wrapperEl.appendChild(slideEl);
    });

    if (wrapperEl.children.length > 0) {
      sliderEl.appendChild(wrapperEl);

      const paginationEl = document.createElement('div');
      paginationEl.className = 'swiper-pagination';
      sliderEl.appendChild(paginationEl);

      cantineSection.appendChild(sliderEl);
      extrasWrapper.appendChild(cantineSection);
    } else {
      cantineSection.innerHTML = cantineSource.innerHTML;
      extrasWrapper.appendChild(cantineSection);
    }
  }

  if (extrasWrapper.children.length > 0) {
    mobileNavContainer.appendChild(extrasWrapper);
    
    // Imposta stato iniziale dello slider dei loghi (nascosto e spostato verso il basso)
    const cantineSection = extrasWrapper.querySelector('.mobile-cantine-logos');
    if (cantineSection) {
      gsap.set(cantineSection, {
        opacity: 0,
        visibility: 'hidden',
        y: 60
      });
    }
  }
  
  // Nascondi immediatamente tutti i link per evitare che siano visibili al primo caricamento
  // Escludi i link dello slider delle cantine che devono essere visibili
  const mobileLinks = mobileNavContainer.querySelectorAll('a:not(.mobile-cantine-logos .swiper-slide a)');
  gsap.set(mobileLinks, { 
    opacity: 0,
    visibility: 'hidden'
  });
  
  // Nascondi i link dello slider al refresh - appariranno con l'animazione
  const cantineLinks = mobileNavContainer.querySelectorAll('.mobile-cantine-logos .swiper-slide a');
  if (cantineLinks.length) {
    gsap.set(cantineLinks, { 
      opacity: 0,
      transform: 'none',
      y: 0,
      x: 0,
      scale: 1,
      visibility: 'hidden'
    });
  }
  
  // Aggiungi al header-content
  headerContent.appendChild(mobileNavContainer);
  

}

function toggleMenu() {
  if (isMenuOpen) {
    closeMenu();
  } else {
    openMenu();
  }
}

function openMenu() {
  if (isMenuOpen) return;
  
  isMenuOpen = true;
  
  // Switch to mobile menu open logo when menu opens
  switchToMobileMenuOpenLogo();
  
  // Mostra il selettore lingua nell'header
  const langSwitcher = document.querySelector('.header-lang-switcher');
  if (langSwitcher) {
    langSwitcher.classList.add('is-visible');
  }
  const mobScrollbar = document.querySelector('.swiper-scrollbar-horizontal');
  // Aggiorna aria-expanded
  hamburgerButton.setAttribute('aria-expanded', 'true');
  
  // Aggiungi classi
  hamburgerButton.classList.add('is-active');
  mobileNav.classList.add('is-active');

  applyMobileNavDimensions();
  enableMenuScrollLock();
  initCantineLogosSlider();
  
  // Imposta stato iniziale del menu: trasparente e posizionato dall'inizio della pagina (dall'alto)
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  gsap.set(mobileNav, { 
    //opacity: 0,
    y: -viewportHeight // Posizione iniziale dall'inizio della pagina (completamente fuori dallo schermo in alto)
  });

  gsap.set(mobScrollbar, {
    opacity: 0,
    visibility: 'hidden'
  });
  
  // Prima fase: anima l'ingresso del container dall'inizio della pagina verso il basso con fade
  gsap.to(mobileNav, {
    opacity: 1,
    y: 0,
    duration: 0.75,
    ease: 'power2.in',
    onComplete: () => {
      // Seconda fase: anima i link con effetto split-text
      if (cantineSwiperInstance) {
        cantineSwiperInstance.update();
      }
      animateMenuLinks();
      // Anima lo slider dei loghi con effetto fade-up
      animateCantineLogosSlider();
      gsap.to(mobScrollbar, {
        opacity: 1,
        visibility: 'visible',
        duration: 0.2,
        ease: 'power2.in'
      });
    }
  });
}

function cleanupSplitTextInstances() {
  splitTextInstances.forEach(instance => {
    if (instance && instance.revert) {
      instance.revert();
    }
  });
  splitTextInstances = [];
}

function animateMenuLinks() {
  // Seleziona solo i link del menu principale (escludi i loghi delle cantine)
  const menuLinks = mobileNav.querySelectorAll('a:not(.mobile-cantine-logos .swiper-slide a)');
  
  // Pulisci eventuali istanze precedenti
  cleanupSplitTextInstances();
  
  // Imposta lo stato iniziale: nascosti e traslati verso il basso
  gsap.set(menuLinks, {
    opacity: 0,
    visibility: 'hidden',
    y: 35
  });
  
  // Anima i link con lo stesso stile dei loghi cantine: fade-in con stagger
  gsap.to(menuLinks, {
    opacity: 1,
    visibility: 'visible',
    y: 0,
    duration: 0.6,
    ease: 'power2.out',
    stagger: 0.05
  });
}

function closeMenu() {
  if (!isMenuOpen) return;
  
  isMenuOpen = false;
  
  // Switch back to appropriate logo when menu closes
  if (window.innerWidth <= 1024) {
    // On mobile, go back to regular mobile logo
    switchToMobileLogo();
  } else {
    // On desktop, go back to desktop logo
    switchToDesktopLogo();
  }
  
  // Nascondi il selettore lingua nell'header
  const langSwitcher = document.querySelector('.header-lang-switcher');
  if (langSwitcher) {
    langSwitcher.classList.remove('is-visible');
  }
  
  // Aggiorna aria-expanded
  hamburgerButton.setAttribute('aria-expanded', 'false');
  
  // Rimuovi classi
  hamburgerButton.classList.remove('is-active');
  
  // Prima fase: anima out i link del menu (escludi i loghi delle cantine)
  const menuLinks = mobileNav.querySelectorAll('a:not(.mobile-cantine-logos .swiper-slide a)');
  
  // Anima anche lo slider dei loghi cantine in parallelo
  const cantineSection = mobileNav.querySelector('.mobile-cantine-logos');
  if (cantineSection) {
    gsap.to(cantineSection, {
      opacity: 0,
      visibility: 'hidden',
      y: 35,
      duration: 0.4,
      ease: 'power2.in'
    });
  }
  
  // Anima reverse dei link con stagger (dal basso verso l'alto) - stesso stile dei loghi cantine
  gsap.to(menuLinks, {
    opacity: 0,
    visibility: 'hidden',
    y: 35,
    duration: 0.4,
    ease: 'power2.in',
    stagger: {
      each: 0.05,
      from: 'end' // Inizia dall'ultimo link
    },
    onComplete: () => {
      // Seconda fase: anima l'uscita del container verso l'inizio della pagina (verso l'alto) con fade
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      gsap.to(mobileNav, {
        //opacity: 0,
        y: -viewportHeight,
        duration: 0.5,
        ease: 'power2.in',
        onComplete: () => {
          mobileNav.classList.remove('is-active');
          clearMobileNavDimensions();
          disableMenuScrollLock();
          // Pulisci le istanze SplitText
          cleanupSplitTextInstances();
          
          // Nascondi nuovamente i link per la prossima apertura
          const menuLinks = mobileNav.querySelectorAll('a');
          gsap.set(menuLinks, { 
            opacity: 0,
            visibility: 'hidden'
          });
          
          // Reimposta l'opacità dei loghi cantine
          resetCantineLogosOpacity();
          
          // Reset dello slider per la prossima apertura
          resetCantineLogosSlider();
          
          // Reset della posizione del menu per la prossima apertura
          gsap.set(mobileNav, {
            y: -viewportHeight
          });
        }
      });
    }
  });
}

function resetCantineLogosSlider() {
  if (!mobileNav) return;
  
  const cantineSection = mobileNav.querySelector('.mobile-cantine-logos');
  if (!cantineSection) return;
  
  // Reset allo stato iniziale per la prossima apertura
  gsap.set(cantineSection, {
    opacity: 0,
    visibility: 'hidden',
    y: 35
  });
}

function handleMobileNavigation() {
  const mobileLinks = mobileNav.querySelectorAll('a');
  
  mobileLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');
      
      // Skip per link esterni, email, telefono, anchor
      if (!href || 
          href.startsWith('mailto:') || 
          href.startsWith('tel:') ||
          href.startsWith('#') ||
          (href.startsWith('http') && !href.includes(window.location.hostname))) {
        return;
      }
      
      // Skip se è la stessa pagina
      if (href === window.location.pathname) {
        closeMenu();
        return;
      }
      
      // Per link interni, gestisci con Swup
      if (href.startsWith('/') || href.startsWith(window.location.origin) || 
          (!href.startsWith('http') && !href.includes('://'))) {
        
        e.preventDefault();
        
        // Chiudi il menu prima di navigare
        closeMenu();
        
        // Avvia l'animazione del cursor se disponibile
        if (window.startCursorLoading && !window.isCursorLoading()) {
          window.startCursorLoading();
        }
        
        // Naviga con Swup dopo un breve delay per permettere la chiusura del menu
        setTimeout(() => {
          if (window.swup) {
            window.swup.navigate(href);
          } else {
            window.location.href = href;
          }
        }, 200);
      }
    });
  });
}

function handleResize() {
  // Chiudi il menu se la finestra diventa più grande di 1024px
  if (window.innerWidth > 1024 && isMenuOpen) {
    closeMenu();
  }
  
  // Reset logo state on resize to ensure proper display
  if (window.innerWidth <= 1024) {
    // On mobile, show mobile logo (not menu open logo unless menu is open)
    if (isMenuOpen) {
      switchToMobileMenuOpenLogo();
      applyMobileNavDimensions();
    } else {
      switchToMobileLogo();
    }
  } else {
    // On desktop, ensure desktop logo is visible
    switchToDesktopLogo();
  }

  if (cantineSwiperInstance) {
    cantineSwiperInstance.update();
  }
}

function handleEscKey(e) {
  if (e.key === 'Escape' && isMenuOpen) {
    closeMenu();
  }
}

function cleanup() {
  // Rimuovi event listeners precedenti se esistono
  const oldButton = document.querySelector('.hb-menu');
  
  if (oldButton) {
    oldButton.removeEventListener('click', toggleMenu);
  }
  
  window.removeEventListener('resize', handleResize);
  document.removeEventListener('keydown', handleEscKey);
  
  // Pulisci le istanze SplitText
  cleanupSplitTextInstances();

  if (cantineSwiperInstance) {
    cantineSwiperInstance.destroy(true, true);
    cantineSwiperInstance = null;
  }
  
  // Reset stato
  isMenuOpen = false;
  disableMenuScrollLock();
  clearMobileNavDimensions();
}

// Logo switching functions
function switchToMobileLogo() {
  const headerLeft = document.querySelector('.header-left');
  if (headerLeft) {
    // Remove menu-open class to show regular mobile logo
    headerLeft.classList.remove('menu-open');
  }
}

function switchToDesktopLogo() {
  const headerLeft = document.querySelector('.header-left');
  if (headerLeft) {
    // Remove menu-open class to show desktop logo
    headerLeft.classList.remove('menu-open');
  }
}

function switchToMobileMenuOpenLogo() {
  const headerLeft = document.querySelector('.header-left');
  if (headerLeft) {
    // Add menu-open class to show mobile menu open logo
    headerLeft.classList.add('menu-open');
  }
}

function initLogoState() {
  // Initialize logo state based on screen size
  if (window.innerWidth <= 1024) {
    // On mobile, show mobile logo by default (not menu open logo)
    switchToMobileLogo();
  } else {
    // On desktop, show desktop logo by default
    switchToDesktopLogo();
  }
} 

function initCantineLogosInteraction() {
  if (!mobileNav) return;
  const cantineLinks = mobileNav.querySelectorAll('.mobile-cantine-logos .swiper-slide a');
  if (!cantineLinks.length) return;

  // I link vengono animati quando il menu si apre, non qui
  // Questa funzione gestisce solo le interazioni al click

  cantineLinks.forEach((link) => {
    link.addEventListener('click', () => {
      // Evidenzia il logo selezionato e attenua gli altri
      cantineLinks.forEach((l) => {
        if (l === link) {
          gsap.to(l, { opacity: 1, duration: 0.2, overwrite: true });
          l.classList.add('selected');
        } else {
          gsap.to(l, { opacity: 0.3, duration: 0.2, overwrite: true });
          l.classList.remove('selected');
        }
      });
    }, { passive: true });
  });
}

function resetCantineLogosOpacity() {
  if (!mobileNav) return;
  const cantineLinks = mobileNav.querySelectorAll('.mobile-cantine-logos .swiper-slide a');
  if (!cantineLinks.length) return;
  cantineLinks.forEach((l) => {
    l.classList.remove('selected');
  });
  // Nascondi i link quando il menu si chiude, così saranno nascosti al refresh
  gsap.set(cantineLinks, { 
    opacity: 0,
    transform: 'none',
    y: 0,
    x: 0,
    scale: 1,
    visibility: 'hidden'
  });
}

function animateCantineLogosSlider() {
  if (!mobileNav) return;
  
  const cantineSection = mobileNav.querySelector('.mobile-cantine-logos');
  if (!cantineSection) return;
  
  const cantineLinks = mobileNav.querySelectorAll('.mobile-cantine-logos .swiper-slide a');
  
  // Assicurati che lo stato iniziale sia impostato per il container
  gsap.set(cantineSection, {
    opacity: 0,
    visibility: 'hidden',
    y: 35
  });
  
  // Assicurati che i link siano nascosti inizialmente
  if (cantineLinks.length) {
    gsap.set(cantineLinks, {
      opacity: 0,
      visibility: 'hidden',
      transform: 'none',
      y: 0,
      x: 0,
      scale: 1
    });
  }
  
  // Calcola il delay basato sul numero di link del menu per animare dopo i link
  const menuLinks = mobileNav.querySelectorAll('a:not(.mobile-cantine-logos .swiper-slide a)');
  const delay = Math.min(menuLinks.length * 100 + 100, 500); // Max 500ms di delay
  
  // Usa setTimeout per assicurarsi che l'animazione parta dopo il delay
  setTimeout(() => {
    // Verifica che l'elemento esista ancora e che il menu sia ancora aperto
    if (!mobileNav || !isMenuOpen) return;
    const section = mobileNav.querySelector('.mobile-cantine-logos');
    if (!section) return;

    // Anima il container con effetto fade-up
    gsap.to(section, {
      opacity: 1,
      visibility: 'visible',
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
      force3D: true,
      onStart: () => {
        // Quando il container inizia ad apparire, anima anche i link
        if (cantineLinks.length) {
          gsap.to(cantineLinks, {
            opacity: 1,
            visibility: 'visible',
            duration: 0.6,
            ease: 'power2.out',
            stagger: 0.05
          });
        }
      },
      onComplete: () => {
        // Assicurati che l'elemento sia visibile dopo l'animazione
        gsap.set(section, { clearProps: 'visibility,opacity' });
      }
    });
  }, delay);
}

function initCantineLogosSlider() {
  if (!mobileNav) return;

  const sliderEl = mobileNav.querySelector('.mobile-cantine-swiper');
  if (!sliderEl) return;

  const slides = sliderEl.querySelectorAll('.swiper-slide');
  if (!slides.length) return;

  if (cantineSwiperInstance) {
    cantineSwiperInstance.destroy(true, true);
    cantineSwiperInstance = null;
  }

  const paginationEl = sliderEl.querySelector('.swiper-pagination');

  const swiperConfig = {
    modules: [Scrollbar],
    slidesPerView: 2.6,
    spaceBetween: 0,
    watchOverflow: true,
    observer: true,
    observeParents: true,
    centeredSlides: true,
    scrollbar: {
      el: '.mobile-cantine-swiper .swiper-scrollbar',
      type: 'scrollbar',
      clickable: true,
    },
  };

  cantineSwiperInstance = new Swiper(sliderEl, swiperConfig);
}

function applyMobileNavDimensions() {
  if (!mobileNav || !isMenuOpen) return;

  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  mobileNav.style.height = `${viewportHeight}px`;
  mobileNav.style.minHeight = `${viewportHeight}px`;
  mobileNav.style.maxHeight = `${viewportHeight}px`;
  mobileNav.style.overscrollBehavior = 'contain';
  mobileNav.style.touchAction = 'none';
  mobileNav.style.webkitOverflowScrolling = 'auto';
}

function clearMobileNavDimensions() {
  if (!mobileNav) return;

  mobileNav.style.height = '';
  mobileNav.style.minHeight = '';
  mobileNav.style.maxHeight = '';
  mobileNav.style.overscrollBehavior = '';
  mobileNav.style.touchAction = '';
  mobileNav.style.webkitOverflowScrolling = '';
}

function enableMenuScrollLock() {
  if (!mobileNav) return;

  mobileNav.addEventListener('wheel', preventMenuScrollHandler, { passive: false });
  mobileNav.addEventListener('touchmove', preventMenuScrollHandler, { passive: false });
}

function disableMenuScrollLock() {
  if (!mobileNav) return;

  mobileNav.removeEventListener('wheel', preventMenuScrollHandler);
  mobileNav.removeEventListener('touchmove', preventMenuScrollHandler);
}